import React, { createContext, useContext, useReducer, useEffect } from "react";
import authService from "../services/authService";
import { USER_ROLES } from "../utils/constants";
import {
  canDelete,
  canCreate,
  canUpdate,
  canRead,
  canManageUsers,
  isAdmin as checkIsAdmin,
  isOperator as checkIsOperator,
  isViewer as checkIsViewer,
  canWrite,
  hasPermission as checkPermission,
  getPermissions,
} from "../utils/permissions";

// Estado inicial del contexto de autenticación
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Tipos de acciones
const AuthActionTypes = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REFRESH_USER: "REFRESH_USER",
  SET_LOADING: "SET_LOADING",
  CLEAR_ERROR: "CLEAR_ERROR",
};

// Reducer para manejo del estado de autenticación
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActionTypes.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AuthActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AuthActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
      };

    case AuthActionTypes.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };

    case AuthActionTypes.REFRESH_USER:
      return {
        ...state,
        user: action.payload.user,
      };

    case AuthActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };

    case AuthActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Crear el contexto
const AuthContext = createContext();

// Exportar el contexto como named export
export { AuthContext };

// Re-exportar el hook desde hooks/useAuth para compatibilidad
// Esto permite importar desde cualquiera de las dos ubicaciones
export { useAuth } from "../hooks/useAuth";

// Provider del contexto de autenticación
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar autenticación al iniciar la aplicación
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authService.getToken();
        const storedUser = authService.getStoredUser();

        if (token && storedUser) {
          // Solo verificar si el token parece válido
          try {
            // Verificar si el token está expirado (no próximo a expirar)
            const decoded = authService.decodeToken(token);
            if (decoded && decoded.exp) {
              const now = Date.now() / 1000;
              if (decoded.exp < now) {
                // Token expirado, hacer logout
                localStorage.removeItem("sgcm_token");
                localStorage.removeItem("sgcm_user");
                dispatch({
                  type: AuthActionTypes.SET_LOADING,
                  payload: { isLoading: false },
                });
                return;
              }
            }

            // Token válido, usar datos almacenados sin verificar con el servidor
            dispatch({
              type: AuthActionTypes.LOGIN_SUCCESS,
              payload: {
                user: storedUser,
                token,
              },
            });
          } catch (error) {
            console.warn("Error al verificar token:", error);
            // En caso de error, limpiar y continuar como no autenticado
            localStorage.removeItem("sgcm_token");
            localStorage.removeItem("sgcm_user");
            dispatch({
              type: AuthActionTypes.SET_LOADING,
              payload: { isLoading: false },
            });
          }
        } else {
          dispatch({
            type: AuthActionTypes.SET_LOADING,
            payload: { isLoading: false },
          });
        }
      } catch (error) {
        console.error("Error al inicializar autenticación:", error);
        dispatch({
          type: AuthActionTypes.SET_LOADING,
          payload: { isLoading: false },
        });
      }
    };

    initializeAuth();
  }, []);

  // Función para iniciar sesión
  const login = async (email, password) => {
    dispatch({ type: AuthActionTypes.LOGIN_START });

    try {
      const result = await authService.login(email, password);

      dispatch({
        type: AuthActionTypes.LOGIN_SUCCESS,
        payload: {
          user: result.user,
          token: result.token,
        },
      });

      return result;
    } catch (error) {
      dispatch({
        type: AuthActionTypes.LOGIN_FAILURE,
        payload: { error: error.message },
      });
      throw error;
    }
  };

  // Función para cerrar sesión
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn("Error al hacer logout:", error);
    } finally {
      dispatch({ type: AuthActionTypes.LOGOUT });
    }
  };

  // Función para actualizar datos del usuario
  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      dispatch({
        type: AuthActionTypes.REFRESH_USER,
        payload: { user: currentUser },
      });
      return currentUser;
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      throw error;
    }
  };

  // Función para cambiar contraseña
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Función para limpiar errores
  const clearError = () => {
    dispatch({ type: AuthActionTypes.CLEAR_ERROR });
  };

  // Función para verificar permisos (mantenida por compatibilidad)
  const hasPermission = (requiredRole) => {
    if (!state.user) return false;

    if (requiredRole === USER_ROLES.ADMIN) {
      return state.user.role === USER_ROLES.ADMIN;
    }

    // Los viewers tienen acceso a funciones de viewer, los admins a todo
    return state.user.role === requiredRole || state.user.role === USER_ROLES.ADMIN;
  };

  // Verificar si es administrador
  const isAdmin = () => {
    return checkIsAdmin(state.user?.role);
  };

  // Verificar si es operador
  const isOperator = () => {
    return checkIsOperator(state.user?.role);
  };

  // Verificar si es viewer
  const isViewer = () => {
    return checkIsViewer(state.user?.role);
  };

  // Verificar si puede eliminar (solo admin)
  const userCanDelete = () => {
    return canDelete(state.user?.role);
  };

  // Verificar si puede crear
  const userCanCreate = () => {
    return canCreate(state.user?.role);
  };

  // Verificar si puede actualizar
  const userCanUpdate = () => {
    return canUpdate(state.user?.role);
  };

  // Verificar si puede leer
  const userCanRead = () => {
    return canRead(state.user?.role);
  };

  // Verificar si puede gestionar usuarios
  const userCanManageUsers = () => {
    return canManageUsers(state.user?.role);
  };

  // Verificar si puede escribir (crear o actualizar)
  const userCanWrite = () => {
    return canWrite(state.user?.role);
  };

  // Obtener todos los permisos del usuario actual
  const getUserPermissions = () => {
    return getPermissions(state.user?.role);
  };

  // Verificar permiso específico usando el sistema de permisos
  const checkUserPermission = (permission) => {
    return checkPermission(state.user?.role, permission);
  };

  // Valor del contexto
  const contextValue = {
    // Estado y dispatch para hooks personalizados
    state,
    dispatch,

    // Estado expandido para acceso directo
    ...state,

    // Acciones
    login,
    logout,
    refreshUser,
    changePassword,
    clearError,

    // Utilidades de roles (compatibilidad + nuevas funciones)
    hasPermission,
    isAdmin,
    isOperator,
    isViewer,

    // Utilidades de permisos específicos
    canDelete: userCanDelete,
    canCreate: userCanCreate,
    canUpdate: userCanUpdate,
    canRead: userCanRead,
    canManageUsers: userCanManageUsers,
    canWrite: userCanWrite,
    getUserPermissions,
    checkPermission: checkUserPermission,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthContext;
